import React, { useCallback, useEffect, useRef, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	Collapse,
	IconButton,
	LinearProgress,
	Paper,
	Stack,
	Step,
	StepLabel,
	Stepper,
	Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import { GlobalState } from "context/GlobalContext";
import { AuthState } from "context/AuthContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { cardSx, primaryButtonSx, SectionCard } from "../sharedStyles";

const POLL_INTERVAL_MS = 5000;

const STATUS_CHIP_COLORS = {
	idle: "default",
	started: "info",
	running: "info",
	completed: "success",
	failed: "error",
};

const STATUS_ICON_COLORS = {
	idle: "#757575",
	started: "#0288d1",
	running: "#0288d1",
	completed: "#2e7d32",
	failed: "#d32f2f",
};

const WORKFLOW_STEPS = ["Upload CSV", "Save to BigQuery", "Apply Offers"];

const STATUS_LABELS = {
	idle: "Idle",
	started: "In Progress",
	running: "In Progress",
	completed: "Completed",
	failed: "Failed",
};

const STEP_LABELS = {
	starting: "Starting",
	loading_sale_data: "Loading sale data",
	loading_discount_schemes: "Loading discount schemes",
	loading_offers: "Loading offers",
	building_offer_file: "Building offer file",
	matching_offers: "Matching offers",
	applying_offers: "Applying offers",
	preparing_output: "Preparing output",
	saving_results: "Saving results",
	saving_to_bq: "Saving results",
	completed: "Completed",
	failed: "Failed",
};

const EXPECTED_COLUMNS = [
	"STORE CODE",
	"DISTRIBUTORS",
	"G-SHORT NAME",
	"G-CODE",
	"BILLNO",
	"BILLDATE",
	"MRP",
	"BILLQTY",
	"MRP VALUE",
	"DISCOUNT",
	"NETAMT",
	"BARCODE",
];

function formatStatusLabel(status) {
	const key = String(status || "").trim().toLowerCase();
	return STATUS_LABELS[key] || "In Progress";
}

function formatStepLabel(step) {
	const key = String(step || "")
		.trim()
		.toLowerCase()
		.replace(/\s+/g, "_");
	if (!key) return "Waiting to start";
	if (STEP_LABELS[key]) return STEP_LABELS[key];
	return key
		.replace(/apply_mbo_offers/g, "offer application")
		.replace(/_/g, " ")
		.replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatUserMessage(message) {
	if (!message) return "";
	return String(message)
		.replace(/apply_mbo_offers/gi, "Offer application")
		.replace(/apply_offers/gi, "Offer application")
		.replace(/mbo sale data/gi, "Sale data")
		.replace(/\s+/g, " ")
		.trim();
}

function formatStatValue(value) {
	if (value === null || value === undefined || value === "") return "-";
	const numeric = Number(value);
	if (Number.isFinite(numeric) && String(value).trim() !== "") {
		return numeric.toLocaleString("en-IN");
	}
	return String(value);
}

const ACCENT = {
	primary: {
		main: "#212121",
		soft: "rgba(33, 33, 33, 0.04)",
		border: "rgba(33, 33, 33, 0.12)",
	},
	info: {
		main: "#0288d1",
		soft: "rgba(2, 136, 209, 0.06)",
		border: "rgba(2, 136, 209, 0.18)",
	},
	success: {
		main: "#2e7d32",
		soft: "rgba(46, 125, 50, 0.06)",
		border: "rgba(46, 125, 50, 0.18)",
	},
	error: {
		main: "#d32f2f",
		soft: "rgba(211, 47, 47, 0.06)",
		border: "rgba(211, 47, 47, 0.18)",
	},
};

const spinIconSx = {
	animation: "mboSaleSpin 1.2s linear infinite",
	"@keyframes mboSaleSpin": {
		from: { transform: "rotate(0deg)" },
		to: { transform: "rotate(360deg)" },
	},
};

function MetricTile({ icon, label, value, accent = "primary", dense = false }) {
	const colors = ACCENT[accent] || ACCENT.primary;

	return (
		<Box
			sx={{
				p: dense ? 1.25 : 1.75,
				borderRadius: 2,
				border: `1px solid ${colors.border}`,
				bgcolor: colors.soft,
				minWidth: 0,
				width: "100%",
				boxSizing: "border-box",
			}}
		>
			<Stack direction="row" spacing={1.25} alignItems="center">
				{icon ? (
					<Box
						sx={{
							width: dense ? 32 : 36,
							height: dense ? 32 : 36,
							borderRadius: 1.5,
							display: "grid",
							placeItems: "center",
							bgcolor: colors.border,
							color: colors.main,
							flexShrink: 0,
							"& .MuiSvgIcon-root": { fontSize: dense ? 18 : 20 },
						}}
					>
						{icon}
					</Box>
				) : null}
				<Box minWidth={0} flex={1}>
					<Typography
						variant="caption"
						color="text.secondary"
						fontWeight={600}
						sx={{ display: "block", lineHeight: 1.2 }}
					>
						{label}
					</Typography>
					<Typography
						variant={dense ? "subtitle1" : "h6"}
						fontWeight={700}
						lineHeight={1.25}
						sx={{ mt: 0.25, wordBreak: "break-word" }}
					>
						{formatStatValue(value)}
					</Typography>
				</Box>
			</Stack>
		</Box>
	);
}

function CollapsiblePanel({
	open,
	onToggle,
	title,
	subtitle,
	icon,
	chip,
	children,
}) {
	const hasBody = Boolean(children);

	return (
		<Paper elevation={0} sx={{ ...cardSx, mb: 2.5 }}>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				spacing={1.5}
				sx={{
					px: { xs: 2, sm: 2.5 },
					py: 1.75,
					cursor: hasBody ? "pointer" : "default",
					"&:hover": hasBody ? { bgcolor: "rgba(0,0,0,0.015)" } : {},
				}}
				onClick={() => hasBody && onToggle?.()}
			>
				<Stack direction="row" alignItems="center" spacing={1.5} minWidth={0}>
					{icon ? (
						<Box
							sx={{
								width: 40,
								height: 40,
								borderRadius: 2,
								display: "grid",
								placeItems: "center",
								bgcolor: "rgba(33, 33, 33, 0.06)",
								color: "#212121",
								flexShrink: 0,
							}}
						>
							{icon}
						</Box>
					) : null}
					<Box minWidth={0}>
						<Typography variant="subtitle1" fontWeight={700} noWrap>
							{title}
						</Typography>
						{subtitle ? (
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{
									mt: 0.15,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: { xs: "normal", sm: "nowrap" },
								}}
							>
								{subtitle}
							</Typography>
						) : null}
					</Box>
				</Stack>

				<Stack direction="row" alignItems="center" spacing={1} flexShrink={0}>
					{chip}
					{hasBody ? (
						<IconButton
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								onToggle?.();
							}}
							aria-label={open ? "Collapse" : "Expand"}
						>
							{open ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
						</IconButton>
					) : null}
				</Stack>
			</Stack>

			{hasBody ? (
				<Collapse in={open} timeout="auto" unmountOnExit={false}>
					<Box
						sx={{
							px: { xs: 2, sm: 2.5 },
							pb: { xs: 2, sm: 2.5 },
							pt: 0.5,
							borderTop: "1px solid #f0f0f0",
						}}
					>
						{children}
					</Box>
				</Collapse>
			) : null}
		</Paper>
	);
}

function renderStatusIcon(status, isApplying) {
	if (status === "completed") return <CheckCircleOutlineIcon />;
	if (status === "failed") return <ErrorOutlineIcon />;
	if (isApplying) return <AutorenewIcon sx={spinIconSx} />;
	return <InfoOutlinedIcon />;
}

function SaleDataWorking() {
	const { setSnackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { user } = AuthState();
	const isAdmin = Boolean(user?.role?.ADMIN || user?.role?.["ADMIN"]);

	const [selectedFile, setSelectedFile] = useState(null);
	const [uploadedFileName, setUploadedFileName] = useState("");
	const [uploadResult, setUploadResult] = useState(null);
	const [statusError, setStatusError] = useState("");
	const [applyResult, setApplyResult] = useState(null);
	const [isDragOver, setIsDragOver] = useState(false);
	const [downloadingSample, setDownloadingSample] = useState(false);
	const [forceStarting, setForceStarting] = useState(false);
	const [columnsOpen, setColumnsOpen] = useState(false);
	const [progressOpen, setProgressOpen] = useState(true);
	const [jobStatus, setJobStatus] = useState({
		status: "idle",
		progress_percent: 0,
		current_step: null,
		message: null,
	});

	const pollTimeoutRef = useRef(null);
	const isPollingRef = useRef(false);
	const startPollingRef = useRef(() => {});
	const fileInputRef = useRef(null);

	const clearPollTimeout = useCallback(() => {
		if (pollTimeoutRef.current) {
			clearTimeout(pollTimeoutRef.current);
			pollTimeoutRef.current = null;
		}
	}, []);

	const showSnack = useCallback(
		(message, severity = "info") => {
			setSnackBarOpen(true);
			setSnackBar((prev) => ({
				...prev,
				message,
				severity,
				anchorOrigin: { horizontal: "center", vertical: "top" },
			}));
		},
		[setSnackBar, setSnackBarOpen]
	);

	const fetchApplyStatus = useCallback(async () => {
		const res = await pythonVmInstance({
			url: "/mbo-payment-working/apply-offers/status",
			method: "GET",
		});
		return res.data;
	}, []);

	const handleStatusResponse = useCallback((data) => {
		const snapshot = data?.job_status || {};
		const applyStatus = data?.apply_offers_status || snapshot.status || "idle";

		setJobStatus({
			status: applyStatus,
			progress_percent: data?.progress_percent ?? snapshot.progress_percent ?? 0,
			current_step: snapshot.current_step,
			message: snapshot.message || data?.message,
			last_started_at: snapshot.last_started_at,
			last_completed_at: snapshot.last_completed_at,
			last_error: snapshot.last_error,
		});

		if (applyStatus === "completed") {
			setApplyResult(snapshot.last_result || data);
			setStatusError("");
			setProgressOpen(true);
			return "completed";
		}

		if (applyStatus === "failed") {
			setStatusError(
				formatUserMessage(data?.message || snapshot.last_error) ||
					"Offer application failed"
			);
			setProgressOpen(true);
			return "failed";
		}

		if (applyStatus === "running" || applyStatus === "started") {
			setProgressOpen(true);
			return "running";
		}

		return "idle";
	}, []);

	const startPolling = useCallback(() => {
		if (isPollingRef.current) return;
		isPollingRef.current = true;

		const poll = async () => {
			try {
				const data = await fetchApplyStatus();
				const state = handleStatusResponse(data);

				if (state === "running") {
					pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
					return;
				}

				isPollingRef.current = false;
				if (state === "failed") {
					showSnack(
						formatUserMessage(data?.message) || "Offer application failed",
						"error"
					);
				}
			} catch (error) {
				isPollingRef.current = false;
				const message =
					formatUserMessage(error?.response?.data?.message) ||
					"Failed to fetch offer application status";
				setStatusError(message);
				showSnack(message, "error");
			}
		};

		poll();
	}, [fetchApplyStatus, handleStatusResponse, showSnack]);

	startPollingRef.current = startPolling;

	useEffect(() => {
		startPollingRef.current();
		return () => {
			clearPollTimeout();
			isPollingRef.current = false;
		};
	}, [clearPollTimeout]);

	const handleDownloadSampleCsv = async () => {
		try {
			setDownloadingSample(true);
			const res = await pythonVmInstance.get(
				"/mbo-payment-working/sale-data/sample-csv",
				{ responseType: "blob" }
			);

			const contentType = res.headers?.["content-type"] || "";
			if (contentType.includes("application/json")) {
				const text = await res.data.text();
				const errorData = JSON.parse(text);
				throw new Error(errorData.message || "Failed to download sample CSV");
			}

			const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", "mbo_sale_data_sample.csv");
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
			showSnack("Sample CSV downloaded", "success");
		} catch (error) {
			let message = "Failed to download sample CSV";
			const data = error?.response?.data;
			if (data instanceof Blob) {
				try {
					const text = await data.text();
					const parsed = JSON.parse(text);
					message = parsed.message || message;
				} catch (_parseError) {
					message = error?.message || message;
				}
			} else if (data?.message) {
				message = data.message;
			} else if (error?.message) {
				message = error.message;
			}
			showSnack(message, "error");
		} finally {
			setDownloadingSample(false);
		}
	};

	const handleForceStartOffers = async () => {
		try {
			setForceStarting(true);
			setStatusError("");
			setApplyResult(null);
			setProgressOpen(true);

			const res = await pythonVmInstance.post(
				"/mbo-payment-working/apply-offers?force=true"
			);

			setJobStatus({
				status: res.data?.apply_offers_status || "running",
				progress_percent: res.data?.job_status?.progress_percent ?? 0,
				current_step: res.data?.job_status?.current_step || "starting",
				message: "Offer application force started.",
			});
			showSnack("Offer application force started.", "success");
			isPollingRef.current = false;
			startPolling();
		} catch (error) {
			const message =
				formatUserMessage(error?.response?.data?.message) ||
				"Failed to force start offer application";
			setStatusError(message);
			showSnack(message, "error");
		} finally {
			setForceStarting(false);
		}
	};

	const acceptFile = (file) => {
		if (!file) return;
		if (!file.name.toLowerCase().endsWith(".csv")) {
			setSelectedFile(null);
			setUploadedFileName("");
			showSnack("Please select a CSV file.", "warning");
			return;
		}
		setSelectedFile(file);
		setUploadedFileName(file.name);
		setUploadResult(null);
		setStatusError("");
		setApplyResult(null);
	};

	const handleFileSelect = (event) => {
		acceptFile(event.target.files?.[0] || null);
		event.target.value = "";
	};

	const handleDrop = (event) => {
		event.preventDefault();
		setIsDragOver(false);
		acceptFile(event.dataTransfer.files?.[0] || null);
	};

	const handleUploadSaleData = async () => {
		if (!selectedFile) {
			showSnack("Please select a sale data CSV file.", "warning");
			return;
		}

		const form = new FormData();
		form.append("file", selectedFile);

		try {
			setLoadingOpen(true);
			setStatusError("");
			setApplyResult(null);

			const res = await pythonVmInstance.post(
				"/mbo-payment-working/upload-sale-data",
				form,
				{ headers: { "Content-Type": "multipart/form-data" } }
			);

			setUploadResult(res.data);
			setSelectedFile(null);
			setUploadedFileName("");

			const applyStatus = res.data?.apply_offers_status;
			if (applyStatus === "started" || applyStatus === "running") {
				setProgressOpen(true);
				setJobStatus({
					status: applyStatus,
					progress_percent: res.data?.job_status?.progress_percent ?? 0,
					current_step: res.data?.job_status?.current_step || "starting",
					message: "Sale data uploaded. Offer application started.",
				});
				showSnack("Sale data uploaded. Offer application started.", "success");
				startPolling();
			} else {
				showSnack("Sale data uploaded successfully", "success");
			}
		} catch (error) {
			const data = error?.response?.data || {};
			const message =
				formatUserMessage(data.message) || "Failed to upload sale data";
			const missingFields = Array.isArray(data.missing_fields)
				? data.missing_fields
				: [];

			setStatusError(message);
			setUploadResult(
				missingFields.length > 0
					? {
							validation_error: true,
							missing_fields: missingFields,
							found_columns: data.found_columns,
						}
					: null
			);
			showSnack(message, "error");
		} finally {
			setLoadingOpen(false);
		}
	};

	const isApplying =
		jobStatus.status === "running" || jobStatus.status === "started";
	const showProgress =
		isApplying ||
		jobStatus.status === "completed" ||
		jobStatus.status === "failed";
	const hasUploadSummary = Boolean(uploadResult && !uploadResult.validation_error);
	const hasLargeResultContent = Boolean(
		applyResult ||
			(uploadResult?.validation_error &&
				(uploadResult.missing_fields || []).length > 0)
	);

	const activeStep =
		jobStatus.status === "completed"
			? 3
			: isApplying
				? 2
				: hasUploadSummary
					? 1
					: 0;

	const statusChipColor = STATUS_CHIP_COLORS[jobStatus.status] || "default";
	const statusIconColor =
		STATUS_ICON_COLORS[jobStatus.status] || STATUS_ICON_COLORS.idle;

	return (
		<Box>
			{/* Compact workflow strip — always small */}
			<Paper
				elevation={0}
				sx={{
					...cardSx,
					px: { xs: 1.5, sm: 2 },
					py: { xs: 1.5, sm: 1.75 },
					mb: 2.5,
				}}
			>
				<Stepper
					activeStep={activeStep}
					alternativeLabel
					sx={{
						"& .MuiStepLabel-label": {
							fontSize: { xs: "0.7rem", sm: "0.8rem" },
							mt: "6px !important",
						},
						"& .MuiStepConnector-line": { minHeight: 2 },
					}}
				>
					{WORKFLOW_STEPS.map((label) => (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>
			</Paper>

			{/* Upload — compact dropzone; details expand on demand */}
			<SectionCard
				title="Upload Sale Data"
				icon={<CloudUploadOutlinedIcon color="primary" />}
				sx={{ mb: 2.5 }}
			>
				<Box
					onClick={() => setColumnsOpen((prev) => !prev)}
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						gap: 1,
						mb: 1.5,
						px: 1.25,
						py: 1,
						borderRadius: 1.5,
						border: "1px solid #eee",
						bgcolor: "#fafafa",
						cursor: "pointer",
						"&:hover": { bgcolor: "#f5f5f5" },
					}}
				>
					<Typography variant="body2" color="text.secondary">
						{columnsOpen
							? "Hide expected columns"
							: "Show expected columns & notes"}
					</Typography>
					{columnsOpen ? (
						<ExpandLessRoundedIcon fontSize="small" color="action" />
					) : (
						<ExpandMoreRoundedIcon fontSize="small" color="action" />
					)}
				</Box>

				<Collapse in={columnsOpen}>
					<Box sx={{ mb: 2 }}>
						<Stack direction="row" flexWrap="wrap" gap={0.75} mb={1.25}>
							{EXPECTED_COLUMNS.map((col) => (
								<Chip
									key={col}
									label={col}
									size="small"
									variant="outlined"
									sx={{
										height: 24,
										fontSize: "0.7rem",
										borderColor: "#e0e0e0",
										bgcolor: "#fff",
									}}
								/>
							))}
						</Stack>
						<Typography variant="caption" color="text.secondary">
							Brand and item details (division, style, HSN, fabric, season,
							COGS) are filled from item master during Apply Offers.
						</Typography>
					</Box>
				</Collapse>

				<Box
					onDragOver={(e) => {
						e.preventDefault();
						setIsDragOver(true);
					}}
					onDragLeave={() => setIsDragOver(false)}
					onDrop={handleDrop}
					onClick={() => !isApplying && fileInputRef.current?.click()}
					sx={{
						border: "1.5px dashed",
						borderColor: isDragOver
							? "#212121"
							: selectedFile
								? "rgba(46, 125, 50, 0.45)"
								: "#e0e0e0",
						borderRadius: 2.5,
						px: { xs: 2, sm: 2.5 },
						py: selectedFile ? 2 : { xs: 2.5, sm: 3 },
						textAlign: "center",
						cursor: isApplying ? "not-allowed" : "pointer",
						bgcolor: isDragOver
							? "rgba(33, 33, 33, 0.04)"
							: selectedFile
								? "rgba(46, 125, 50, 0.04)"
								: "#fafafa",
						transition: "border-color 0.15s ease, background-color 0.15s ease",
						"&:hover": isApplying
							? {}
							: {
									borderColor: "#212121",
									bgcolor: "rgba(33, 33, 33, 0.03)",
								},
					}}
				>
					<input
						ref={fileInputRef}
						type="file"
						hidden
						accept=".csv"
						onChange={handleFileSelect}
						disabled={isApplying}
					/>

					{selectedFile ? (
						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={1.5}
							alignItems="center"
							justifyContent="center"
						>
							<Box
								sx={{
									width: 40,
									height: 40,
									borderRadius: 2,
									display: "grid",
									placeItems: "center",
									bgcolor: "rgba(46, 125, 50, 0.12)",
									color: "#2e7d32",
									flexShrink: 0,
								}}
							>
								<InsertDriveFileOutlinedIcon />
							</Box>
							<Box minWidth={0} textAlign={{ xs: "center", sm: "left" }}>
								<Typography
									variant="subtitle2"
									fontWeight={700}
									sx={{
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
										maxWidth: { xs: "100%", sm: 360 },
									}}
								>
									{uploadedFileName}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									Click to choose a different file
								</Typography>
							</Box>
						</Stack>
					) : (
						<>
							<Box
								sx={{
									width: 44,
									height: 44,
									mx: "auto",
									mb: 1,
									borderRadius: "50%",
									display: "grid",
									placeItems: "center",
									bgcolor: "rgba(33, 33, 33, 0.07)",
									color: "#212121",
								}}
							>
								<CloudUploadOutlinedIcon />
							</Box>
							<Typography variant="subtitle2" fontWeight={700}>
								Drop CSV here or click to browse
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ display: "block", mt: 0.35 }}
							>
								.csv only
							</Typography>
						</>
					)}
				</Box>

				<Stack
					direction={{ xs: "column", sm: "row" }}
					spacing={1.25}
					mt={2}
					alignItems="stretch"
				>
					<Button
						variant="contained"
						fullWidth
						onClick={handleUploadSaleData}
						disabled={!selectedFile || isApplying}
						startIcon={<CloudUploadOutlinedIcon />}
						sx={{
							py: 1.1,
							borderRadius: 2,
							fontWeight: 700,
							...primaryButtonSx,
						}}
					>
						Upload & Apply Offers
					</Button>

					<Button
						variant="outlined"
						fullWidth
						onClick={handleDownloadSampleCsv}
						disabled={downloadingSample}
						startIcon={<DownloadOutlinedIcon />}
						sx={{ py: 1.05, borderRadius: 2, fontWeight: 600 }}
					>
						{downloadingSample ? "Downloading..." : "Sample CSV"}
					</Button>

					{isAdmin ? (
						<Button
							variant="outlined"
							color="warning"
							fullWidth
							onClick={handleForceStartOffers}
							disabled={forceStarting || isApplying}
							startIcon={<BoltOutlinedIcon />}
							sx={{ py: 1.05, borderRadius: 2, fontWeight: 700 }}
						>
							{forceStarting ? "Starting..." : "Force Start"}
						</Button>
					) : null}
				</Stack>

				{/* Validation errors expand to large content when present */}
				{uploadResult?.validation_error ? (
					<Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
						<Typography variant="body2" fontWeight={700} gutterBottom>
							{statusError}
						</Typography>
						{(uploadResult.missing_fields || []).length > 0 ? (
							<>
								<Typography variant="body2" sx={{ mb: 1 }}>
									Each required field must match one of the accepted column
									names:
								</Typography>
								<Stack
									component="ul"
									spacing={0.5}
									sx={{
										m: 0,
										pl: 2,
										maxHeight: hasLargeResultContent ? 220 : "none",
										overflow: "auto",
									}}
								>
									{(uploadResult.missing_fields || []).map((field) => (
										<Typography
											key={field.field}
											component="li"
											variant="body2"
										>
											<strong>{field.field}</strong>:{" "}
											{(field.accepted_columns || []).join(", ")}
										</Typography>
									))}
								</Stack>
							</>
						) : null}
					</Alert>
				) : null}
			</SectionCard>

			{/* Upload summary — always visible after a successful upload */}
			{hasUploadSummary ? (
				<Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, sm: 2.5 }, mb: 2.5 }}>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						mb={1.75}
						flexWrap="wrap"
						gap={1}
					>
						<Stack direction="row" alignItems="center" spacing={1.25} minWidth={0}>
							<Box
								sx={{
									width: 40,
									height: 40,
									borderRadius: 2,
									display: "grid",
									placeItems: "center",
									bgcolor: "rgba(46, 125, 50, 0.12)",
									color: "#2e7d32",
									flexShrink: 0,
								}}
							>
								<CheckCircleOutlineIcon fontSize="small" />
							</Box>
							<Box minWidth={0}>
								<Typography variant="subtitle1" fontWeight={700}>
									Upload Summary
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{formatStatValue(uploadResult.rows_uploaded)} rows ·{" "}
									{formatStatValue(uploadResult.keys_deleted)} keys replaced
								</Typography>
							</Box>
						</Stack>
						<Chip
							label="Uploaded"
							color="success"
							size="small"
							sx={{ fontWeight: 700 }}
						/>
					</Stack>

					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "1fr",
								sm: "repeat(3, minmax(0, 1fr))",
							},
							gap: 1.25,
						}}
					>
						<MetricTile
							dense
							label="Rows Uploaded"
							value={uploadResult.rows_uploaded}
						/>
						<MetricTile
							dense
							label="Keys Replaced"
							value={uploadResult.keys_deleted}
						/>
						<MetricTile
							dense
							label="Offer Status"
							value={formatStatusLabel(uploadResult.apply_offers_status)}
							accent="info"
						/>
					</Box>
				</Paper>
			) : null}

			{/* Progress / results — slim header when idle-ish; expands for large result content */}
			<Collapse in={showProgress}>
				<CollapsiblePanel
					open={progressOpen}
					onToggle={() => setProgressOpen((prev) => !prev)}
					title="Offer Application"
					subtitle={formatStepLabel(jobStatus.current_step)}
					icon={
						<Box sx={{ color: statusIconColor, display: "grid", placeItems: "center" }}>
							{renderStatusIcon(jobStatus.status, isApplying)}
						</Box>
					}
					chip={
						<Chip
							label={formatStatusLabel(jobStatus.status)}
							color={statusChipColor}
							size="small"
							sx={{ fontWeight: 700 }}
						/>
					}
				>
					<Box sx={{ mb: applyResult || statusError ? 2 : 0 }}>
						<Stack direction="row" justifyContent="space-between" mb={0.75}>
							<Typography
								variant="body2"
								color="text.secondary"
								fontWeight={500}
							>
								Progress
							</Typography>
							<Typography variant="body2" fontWeight={700}>
								{jobStatus.progress_percent ?? 0}%
							</Typography>
						</Stack>
						<LinearProgress
							variant="determinate"
							value={jobStatus.progress_percent ?? 0}
							sx={{
								height: 8,
								borderRadius: 4,
								bgcolor: "rgba(33, 33, 33, 0.08)",
								"& .MuiLinearProgress-bar": { borderRadius: 4 },
							}}
						/>
					</Box>

					{formatUserMessage(jobStatus.message) &&
					jobStatus.status !== "completed" ? (
						<Alert
							severity={jobStatus.status === "failed" ? "error" : "info"}
							icon={false}
							sx={{ borderRadius: 2, mb: 2, py: 0.5 }}
						>
							<Typography variant="body2">
								{formatUserMessage(jobStatus.message)}
							</Typography>
						</Alert>
					) : null}

					{statusError && jobStatus.status === "failed" ? (
						<Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
							{statusError}
						</Alert>
					) : null}

					{applyResult ? (
						<Box>
							<Alert
								severity="success"
								icon={<CheckCircleOutlineIcon fontSize="small" />}
								sx={{ borderRadius: 2, mb: 1.75, py: 0.25 }}
							>
								Offer application completed successfully.
							</Alert>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: {
										xs: "1fr",
										sm: "repeat(3, minmax(0, 1fr))",
									},
									gap: 1.25,
								}}
							>
								<MetricTile
									icon={<TableChartOutlinedIcon />}
									label="Sale Rows"
									value={applyResult.sale_rows}
								/>
								<MetricTile
									icon={<LocalOfferOutlinedIcon />}
									label="Offer Rows"
									value={applyResult.offer_rows}
									accent="info"
								/>
								<MetricTile
									icon={<StorageOutlinedIcon />}
									label="Output Rows"
									value={applyResult.output_rows ?? applyResult.rows_saved}
									accent="success"
								/>
							</Box>
						</Box>
					) : null}
				</CollapsiblePanel>
			</Collapse>
		</Box>
	);
}

export default SaleDataWorking;
